import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useMotionValue } from 'framer-motion';
import type { DrawnCard } from '@tarot/shared';
import { randomReversed, shuffleDeck } from '../lib/draw';
import { TarotCard } from './TarotCard';
import { LogoMark } from '../brand/BrandLogo';

/**
 * 실제 타로 숍처럼 78장이 뒤집혀 펼쳐진 벨벳 테이블에서 사용자가 직접 뽑는 UI.
 * 구성: [벨벳 덱 테이블] 과 [리딩 클로스(뽑은 카드 자리)] 두 영역으로 분리.
 * - 좌우 탐색: 스트립 드래그(마우스/터치, 관성). 경계는 ref constraint로 양끝 고정
 * - 뽑기: 카드를 잡고 아래로 PULL_THRESHOLD_PX 이상 끌어내리면 확정
 * - 입장 시 2초간 좌우 이동 가이드, 뽑힌 카드는 클릭 시 라이트박스
 * 스펙: doc/ux-card-picking.md
 */

const PULL_THRESHOLD_PX = 60;
const COMPLETE_DELAY_MS = 700;
const GUIDE_VISIBLE_MS = 2000;
const EDGE_ELASTIC = 0.05;

/**
 * 입장 인트로: 중앙 더미 → 섞임 → 펼쳐짐.
 * 실제 움직임은 index.css의 `tw-card-intro` 키프레임이 담당한다(GPU 처리).
 * 여기서는 "끝났는지"만 알면 되므로 단계별 state를 두지 않는다 —
 * 인트로 중 state가 바뀌면 카드 78장이 통째로 재렌더링돼 프레임이 튄다.
 */
const CARD_STEP_PX = 28; // 카드 폭(72) - 겹침(44) = 스트립에서 카드 간 간격
const INTRO_TOTAL_MS = 2500; // CSS 키프레임 전체 길이와 맞출 것
const INTRO_MAX_DELAY_MS = 8 * 18; // 카드별 순차 지연 최대치

interface CardFanProps {
  count: number;
  positionLabels: string[];
  /**
   * 이미 뽑아둔 카드 (방 재접속 복구용).
   * 새로고침으로 돌아오면 서버 상태에는 내가 뽑은 카드가 남아 있는데 덱은 처음부터 시작한다.
   * 이 값을 넘기면 그 카드들을 뽑힌 상태로 되살리고 덱에서도 빼둔다.
   */
  initialPicked?: DrawnCard[];
  /** 클로스 헤딩 대체 텍스트 — 4장 이상이면 라벨 나열 대신 이걸 표시 (모바일 줄바꿈 방지) */
  title?: string;
  /** count장 모두 뽑았을 때 (솔로 모드) */
  onComplete?: (cards: DrawnCard[]) => void;
  /** 1장 뽑을 때마다 (방 모드 — WS card:pick 전송용) */
  onPick?: (card: DrawnCard) => void;
}

/** 스프레드 장수별 슬롯 그리드 — gap 포함 (SoloPage/RoomPage 해석 화면과 공용) */
export function slotGridClass(count: number): string {
  if (count === 1) return 'mx-auto w-40 grid-cols-1 gap-3';
  if (count <= 3) return 'grid-cols-3 gap-3';
  return 'grid-cols-5 gap-1.5'; // 5장 이상: 좁은 화면에서 잘리지 않게 간격 축소
}

/** 카드 위 포지션 라벨(과거·현재·미래 등) — 고정 높이로 카드 시작 라인을 통일 (긴 라벨 2줄 대응) */
export function slotLabelClass(): string {
  return 'flex h-8 w-full items-center justify-center text-center text-[13px] font-medium leading-[1.15] tracking-wide text-moon-dim';
}

export function CardFan({
  count,
  positionLabels,
  title,
  initialPicked,
  onComplete,
  onPick,
}: CardFanProps) {
  const [deck] = useState(() => shuffleDeck());
  // 재접속 복구: 서버가 기억한 내 카드로 시작한다 (없으면 빈 상태)
  const [picked, setPicked] = useState<DrawnCard[]>(() => initialPicked ?? []);
  const [showGuide, setShowGuide] = useState(false);
  const [ready, setReady] = useState(false);
  const x = useMotionValue(0);
  const viewRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const completedRef = useRef(false);
  const centerIdx = (deck.length - 1) / 2;

  // 인트로가 끝나면 조작 가능 상태로. 중간 단계는 CSS가 알아서 진행한다.
  useEffect(() => {
    const t = setTimeout(() => setReady(true), INTRO_TOTAL_MS + INTRO_MAX_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  // 가이드는 펼쳐진 뒤에 등장 → 2초 후 소멸
  useEffect(() => {
    if (!ready) return;
    setShowGuide(true);
    const t = setTimeout(() => setShowGuide(false), GUIDE_VISIBLE_MS);
    return () => clearTimeout(t);
  }, [ready]);

  /** 스크롤 한계(좌측 끝) — 실측 기반 */
  const minX = () => {
    const view = viewRef.current;
    const strip = stripRef.current;
    if (!view || !strip) return 0;
    return Math.min(0, view.clientWidth - strip.scrollWidth);
  };

  useLayoutEffect(() => {
    // 덱 중앙에서 시작 — 뷰 좌측 패딩(px-4)만큼 보정하지 않으면 살짝 우측으로 치우침
    const view = viewRef.current;
    const padLeft = view ? parseFloat(getComputedStyle(view).paddingLeft) : 0;
    x.set(minX() / 2 - padLeft);
  }, [x]);

  const pick = (cardId: number) => {
    setPicked((prev) => {
      if (prev.length >= count || prev.some((p) => p.cardId === cardId)) return prev;
      const drawn: DrawnCard = { cardId, reversed: randomReversed(), position: prev.length };
      const next = [...prev, drawn];
      onPick?.(drawn);
      if (next.length === count && onComplete && !completedRef.current) {
        completedRef.current = true;
        setTimeout(() => onComplete(next), COMPLETE_DELAY_MS);
      }
      return next;
    });
  };

  const remaining = count - picked.length;

  return (
    <div className="flex flex-col gap-4">
      {/* ── 덱 테이블 (벨벳 + 촛불 글로우) — 색은 index.css 벨벳 토큰(라이트 대응) ── */}
      <section className="relative overflow-hidden rounded-3xl border-2 border-gold/25 bg-[radial-gradient(130%_110%_at_50%_-10%,var(--color-velvet-1)_0%,var(--color-velvet-2)_45%,var(--color-velvet-3)_100%)] [box-shadow:inset_0_0_46px_var(--shadow-ink),0_10px_30px_var(--shadow-ink)]">
        {/* 촛불 빛 */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[radial-gradient(60%_90%_at_50%_0%,color-mix(in_srgb,var(--color-gold)_22%,transparent),transparent_70%)]" />
        {/* 자수 테두리 */}
        <div className="pointer-events-none absolute inset-2 rounded-[1.25rem] border border-gold/15" />

        <div
          ref={viewRef}
          onWheel={(e) => {
            const delta = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
            x.set(Math.min(0, Math.max(minX(), x.get() - delta)));
          }}
          className="relative overflow-hidden px-4 pt-6"
        >
          <motion.div
            ref={stripRef}
            drag={ready ? 'x' : false}
            dragConstraints={viewRef}
            dragElastic={EDGE_ELASTIC}
            dragMomentum
            style={{ x }}
            className={`flex w-max items-start pb-12 pt-2 ${ready ? 'cursor-grab active:cursor-grabbing' : ''}`}
          >
            <AnimatePresence>
              {deck
                .filter((id) => !picked.some((p) => p.cardId === id))
                .map((id, i) => {
                  // 카드별 편차 — 인트로 동안은 CSS 변수로 넘겨 컴포지터가 처리하게 한다
                  const stackX = (centerIdx - i) * CARD_STEP_PX;
                  const jitter = ((i * 7) % 5) - 2;
                  const dir = i % 2 === 0 ? 1 : -1;
                  const throwX = dir * (36 + ((i * 17) % 42));
                  const lift = 16 + ((i * 13) % 30);
                  return (
                    <motion.div
                      key={id}
                      layout={ready}
                      // 인트로 중에는 framer가 transform을 건드리지 않는다 (CSS 애니메이션이 담당).
                      // ready로 넘어가는 순간의 CSS 최종값과 framer 목표값이 같아 이음매가 보이지 않음.
                      {...(ready
                        ? {
                            animate: { x: 0, rotate: jitter },
                            transition: { duration: 0 },
                          }
                        : {})}
                      exit={{ y: 150, opacity: 0, transition: { duration: 0.35 } }}
                      drag={ready ? 'y' : false}
                      dragPropagation
                      dragConstraints={{ top: 0, bottom: 0 }}
                      dragElastic={{ top: 0, bottom: 0.9 }}
                      onDragEnd={(_, info) => {
                        if (info.offset.y > PULL_THRESHOLD_PX) pick(id);
                      }}
                      whileDrag={ready ? { scale: 1.15, zIndex: 50 } : undefined}
                      whileHover={ready ? { y: -12 } : undefined}
                      style={
                        {
                          marginTop: (i * 11) % 7, // 손으로 펼친 듯한 미세 높이차
                          touchAction: 'none',
                          '--stack-x': `${stackX}px`,
                          '--throw': `${throwX}px`,
                          '--lift': `${lift}px`,
                          '--rot': `${dir * (10 + (i % 9))}deg`,
                          '--jitter': `${jitter}deg`,
                          '--intro-ms': `${INTRO_TOTAL_MS}ms`,
                          // 물결처럼 순차로 튀어오름
                          '--intro-delay': `${(i % 9) * 18}ms`,
                        } as React.CSSProperties
                      }
                      className={`-ml-11 w-[4.5rem] shrink-0 first:ml-0 ${ready ? '' : 'tw-card-intro'}`}
                    >
                      <CardBack />
                    </motion.div>
                  );
                })}
            </AnimatePresence>
          </motion.div>

          {/* 입장 가이드 — 2초 */}
          <AnimatePresence>
            {showGuide && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.5 } }}
                className="pointer-events-none absolute inset-0 z-10 flex items-center justify-between bg-night/40 px-4"
              >
                <GuideArrow dir="left" />
                <span className="rounded-full bg-night-2/90 px-4 py-2 text-sm text-moon shadow-lg">
                  좌우로 움직여 카드를 살펴보세요
                </span>
                <GuideArrow dir="right" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p className="relative pb-4 text-center text-xs text-moon-dim">
          {!ready ? (
            <>카드를 섞고 있어요… ✨</>
          ) : remaining > 0 ? (
            <>
              🫳 카드를 잡고 <span className="font-medium text-gold">아래로</span> 끌어내리면 뽑혀요
              <span className="mx-2 opacity-40">|</span>
              <span className="font-semibold text-gold">{remaining}장</span> 남음
            </>
          ) : (
            <>카드를 모두 뽑았어요 ✨</>
          )}
        </p>
      </section>

      {/* ── 리딩 클로스 (뽑은 카드 자리) — 덱과 분리된 영역 ────── */}
      <section className="rounded-3xl border border-gold/15 bg-night-2/50 p-4 [box-shadow:inset_0_0_24px_var(--shadow-ink)]">
        <h3 className="mb-3 flex items-center gap-3 whitespace-nowrap text-[11px] tracking-[0.15em] text-gold/80">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gold/40" />✦{' '}
          {count > 3 && title ? title : positionLabels.join(' · ')} ✦
          <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gold/40" />
        </h3>
        <ul className={`grid ${slotGridClass(count)}`}>
          {positionLabels.map((label, i) => {
            const d = picked[i];
            return (
              // 라벨 길이가 달라도 카드가 같은 라인에 놓이도록: 카드 먼저(위 정렬), 라벨은 아래 고정 높이
              <li key={label} className="flex min-w-0 flex-col items-center gap-1">
                {d ? (
                  <motion.div
                    initial={{ opacity: 0, y: -24, rotateY: 90 }}
                    animate={{ opacity: 1, y: 0, rotateY: 0 }}
                    transition={{ duration: 0.45, ease: 'easeOut' }}
                    className="w-full"
                  >
                    <TarotCard drawn={d} />
                  </motion.div>
                ) : (
                  <div className="flex aspect-[3/5] w-full items-center justify-center rounded-lg border border-dashed border-gold/25 bg-black/25">
                    <span className="text-[10px] text-gold/50">✦</span>
                  </div>
                )}
                <span
                  className={`flex h-8 w-full items-center justify-center text-center text-[13px] font-medium leading-[1.15] ${
                    d ? 'text-gold' : 'text-moon-dim/80'
                  }`}
                >
                  {label}
                </span>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

function GuideArrow({ dir }: { dir: 'left' | 'right' }) {
  const sign = dir === 'left' ? -1 : 1;
  return (
    <motion.span
      animate={{ x: [0, sign * 8, 0] }}
      transition={{ repeat: Infinity, duration: 0.9, ease: 'easeInOut' }}
      className="text-2xl text-gold drop-shadow"
      aria-hidden
    >
      {dir === 'left' ? '◀' : '▶'}
    </motion.span>
  );
}

/** 카드 뒷면 — 이중 골드 프레임 + 은은한 사선 패턴 + 풀 CI(LogoMark) (src/brand 단일 출처, 색은 토큰) */
function CardBack() {
  return (
    <div className="relative aspect-[3/5] w-full overflow-hidden rounded-lg border border-gold/60 bg-[var(--color-card-back)] shadow-md shadow-[var(--shadow-ink)]">
      <div className="absolute inset-[3px] rounded-md border border-gold/30 bg-[repeating-linear-gradient(45deg,color-mix(in_srgb,var(--color-gold)_8%,transparent)_0px,color-mix(in_srgb,var(--color-gold)_8%,transparent)_3px,transparent_3px,transparent_7px)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,color-mix(in_srgb,var(--color-gold)_20%,transparent),transparent_55%)]" />
      <div className="absolute inset-0 flex items-center justify-center">
        <LogoMark className="h-14 w-14 text-gold/85" />
      </div>
    </div>
  );
}
